import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Badge } from './ui/badge'
import { Tabs as ShadcnTabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'

const tabsVariants = cva('min-w-0 w-full', {
  variants: {
    variant: {
      default: '',
      pills: '',
      underline: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const tabsListVariants = cva(
  'inline-flex min-w-0 flex-nowrap items-center justify-start rounded-md p-1 overflow-x-auto overflow-y-hidden text-text-secondary bg-white [-webkit-overflow-scrolling:touch]',
  {
    variants: {
      variant: {
        default: '',
        pills: 'bg-transparent',
        underline: 'bg-transparent rounded-none p-0 gap-0',
      },
      size: {
        default: 'h-auto',
        sm: 'h-auto',
        lg: 'h-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const tabsTriggerVariants = cva(
  'inline-flex flex-shrink-0 flex-col items-center justify-end gap-0.5 text-text-secondary bg-white data-[state=active]:text-text-primary whitespace-nowrap rounded-sm text-sm font-normal ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative',
  {
    variants: {
      variant: {
        default: 'data-[state=active]:text-text-link data-[state=active]:font-medium',
        pills: 'rounded-full data-[state=active]:bg-blue-600 data-[state=active]:text-white',
        underline: 'flex-col items-center justify-end gap-0.5 px-4 !pb-0 h-16 text-center font-normal ',
      },
      size: {
        default: 'px-3 py-1.5 text-sm',
        sm: 'px-2 py-1 text-xs',
        lg: 'px-4 py-2 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface TabItem {
  value: string
  label: string
  badge?: number | string
  disabled?: boolean
  icon?: React.ReactNode
}

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>, VariantProps<typeof tabsVariants> {
  items: TabItem[]
  defaultValue?: string
  value?: string
  onChange?: (value: string) => void
  orientation?: 'horizontal' | 'vertical'
  size?: 'default' | 'sm' | 'lg'
  listClassName?: string
  triggerClassName?: string
  badgeClassName?: string
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      className,
      variant,
      size,
      items,
      defaultValue,
      value,
      onChange,
      orientation = 'horizontal',
      children,
      listClassName,
      triggerClassName,
      badgeClassName,
    },
    ref
  ) => {
    return (
      <ShadcnTabs
        ref={ref}
        defaultValue={defaultValue}
        value={value}
        onValueChange={onChange}
        orientation={orientation}
        className={cn(tabsVariants({ variant, className }))}
      >
        <TabsList className={cn(tabsListVariants({ variant, size }), listClassName)}>
          {items.map(item => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className={cn(tabsTriggerVariants({ variant, size }), triggerClassName)}
              onClick={() => onChange?.(item.value)}
            >
              <div
                className={cn(
                  'relative flex h-full w-fit items-center justify-center gap-2',
                  variant === 'underline' &&
                    value === item.value &&
                    'after:bg-icon-primary px-2 after:absolute after:start-0 after:end-0 after:-bottom-[2px] after:h-[3px] after:rounded-t-sm after:transition-all'
                )}
              >
                <div className='flex flex-col items-center gap-0.5'>
                  {item.icon && <div className={cn('flex-shrink-0')}>{item.icon}</div>}
                  <span className='text-center'>{item.label}</span>
                </div>
                {item.badge !== undefined && (
                  <Badge
                    variant='destructive'
                    className={cn('flex h-5 w-5 items-center justify-center rounded-full p-0 text-xs', badgeClassName)}
                  >
                    {item.badge}
                  </Badge>
                )}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
        {children}
      </ShadcnTabs>
    )
  }
)

Tabs.displayName = 'Tabs'

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsVariants }
